/**
 * Client-side copy of fleet FA cache lookup helpers (mirrors api/fleet-display.mjs).
 * Web build context is ./web — cannot import ../../api/*.
 */

export function cleanFleetText(value) {
  if (value == null) return "";
  return String(value).trim();
}

export function isInternalFleetIdent(value) {
  const v = cleanFleetText(value).toUpperCase();
  if (!v) return false;
  if (/^XRM-/i.test(v)) return true;
  if (/^\d+::[A-Z0-9]+$/i.test(v)) return true;
  return false;
}

export function isPlaceholderRegistration(value) {
  return isInternalFleetIdent(value);
}

export function normalizeRegistrationDisplay(value) {
  const v = cleanFleetText(value);
  if (!v) return "";
  return v.replace(/\.+$/g, "").trim();
}

export function faLookupKeysForAircraft(aircraft) {
  const keys = [];
  const push = (raw) => {
    const v = cleanFleetText(raw);
    if (!v) return;
    const upper = v.toUpperCase();
    const noDash = upper.replace(/-/g, "");
    for (const k of [upper, noDash]) {
      if (k && !keys.includes(k)) keys.push(k);
    }
  };
  push(aircraft?.registration);
  push(aircraft?.tail);
  push(aircraft?.ident);
  return keys;
}

export function indexFaCacheEntry(map, rawKey, entry) {
  const v = cleanFleetText(rawKey);
  if (!v) return;
  const upper = v.toUpperCase();
  const noDash = upper.replace(/-/g, "");
  for (const k of [upper, noDash]) {
    if (k && !map.has(k)) map.set(k, entry);
  }
}

export function buildFaStatusMapFromCacheRows(rows) {
  const map = new Map();
  for (const row of rows || []) {
    let status;
    try {
      status = typeof row.status === "string" ? JSON.parse(row.status) : row.status;
    } catch {
      continue;
    }
    if (!status) continue;
    const entry = { status, fetched_at: row.fetched_at, source: row.source, cacheIdent: row.ident };
    indexFaCacheEntry(map, row.ident, entry);
    indexFaCacheEntry(map, status.registration, entry);
    indexFaCacheEntry(map, status.ident, entry);
  }
  return map;
}

export function lookupFaCacheEntry(aircraft, map) {
  if (!map || !aircraft) return null;
  for (const k of faLookupKeysForAircraft(aircraft)) {
    const hit = map.get(k);
    if (hit) return hit;
  }
  return null;
}

/** Re-index cache rows under registration / ident aliases for UI lookup. */
export function rebuildFaCacheByIdentFromEntries(entries) {
  const canonicalByIdent = new Map();
  for (const entry of entries || []) {
    const ident = cleanFleetText(entry?.ident);
    if (!ident) continue;
    canonicalByIdent.set(ident.toUpperCase(), entry);
  }
  const rows = Array.from(canonicalByIdent.values()).map((entry) => ({
    ident: entry.ident,
    status: entry.status,
    fetched_at: entry.fetched_at,
    source: entry.source || "snapshot",
  }));
  const aliasMap = buildFaStatusMapFromCacheRows(rows);
  const out = new Map();
  for (const [k, v] of aliasMap) {
    const canonicalKey = cleanFleetText(v.cacheIdent || k).toUpperCase();
    const canonical = canonicalByIdent.get(canonicalKey);
    out.set(k, {
      ident: canonical?.ident || v.cacheIdent || k,
      status: v.status,
      fetched_at: v.fetched_at,
      source: v.source,
      response_error: canonical?.response_error ?? null,
    });
  }
  return out;
}
